import React from "react";

import { Flex, type ImageProps, Link, type LinkProps } from "@chakra-ui/react";
import { LogoBrandmark, LogoInline, LogoStacked } from "../../theme/logos";

export interface LogoVariant {
  imageProps: Partial<ImageProps>;
  linkProps?: Partial<LinkProps>;
}

export interface LogoProps extends Omit<ImageProps, "src"> {
  variant: "brandmark" | "inline" | "stacked" | "square";
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "print";
  alt?: string;
  href?: string;
  backgroundColor?: string;
  color?: string | { _light: string; _dark: string };
  hasBreakpointSizes?: boolean;
  isSquare?: boolean;
  isDarkMint?: boolean;
  linkProps?: Omit<LinkProps, "href">;
  src?: string;
}

// Chakra spacing tokens are 4px per unit; e.g. 16 → 64px, 20 → 80px.
// Print uses raw CSS strings because Zeplin specifies exact px that don't
// align with the Chakra scale — 67px is between spacing.16 (64px) and
// spacing.20 (80px). Callers using size="print" must pass
// hasBreakpointSizes={false}; isSquare is not supported for size="print".
const LOGO_SIZES: Record<string, { base: number | string; md: number | string; lg: number | string }> = {
  xs: { base: 4, md: 6, lg: 8 },
  sm: { base: 6, md: 8, lg: 12 },
  md: { base: 8, md: 12, lg: 16 },
  lg: { base: 12, md: 16, lg: 20 },
  xl: { base: 16, md: 20, lg: 24 },
  print: { base: "89px", md: "89px", lg: "89px" }
};

export const Logo: React.FC<LogoProps> = ({
  variant = "inline",
  size = "md",
  alt = "Euka Logo",
  href,
  backgroundColor,
  color,
  hasBreakpointSizes = true,
  isSquare = false,
  isDarkMint = true,
  src: _src,
  linkProps
}) => {
  const logoColor = isDarkMint
    ? { _light: color, _dark: "success.500" }
    : color;
  const logoSize = hasBreakpointSizes ? LOGO_SIZES[size] : LOGO_SIZES[size].md;
  const isLogoSquare = isSquare || variant === "square";
  const asNumber = (v: number | string): number =>
    typeof v === "number" ? v : 0;
  const logoSquarePadding = hasBreakpointSizes
    ? {
        base: asNumber(LOGO_SIZES[size].base) / 8,
        md: asNumber(LOGO_SIZES[size].md) / 8,
        lg: asNumber(LOGO_SIZES[size].lg) / 8
      }
    : asNumber(LOGO_SIZES[size].md) / 8;

  const getLogoVariant = () => {
    if (variant === "stacked") {
      return (
        <LogoStacked
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-stacked"
        />
      );
    } else if (variant === "inline") {
      return (
        <LogoInline
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-inline"
        />
      );
    } else {
      return (
        <LogoBrandmark
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-brandmark"
        />
      );
    }
  };

  return (
    <Flex
      data-bark-component="Logo"
      bgColor={backgroundColor}
      aspectRatio={isLogoSquare ? "1/1" : "auto"}
      p={isLogoSquare ? logoSquarePadding : "0"}
      w="fit-content"
      h="fit-content"
      data-testid="logo-container"
    >
      {href ? (
        <Link href={href} {...linkProps} data-testid="logo-link-wrapper">
          {getLogoVariant()}
        </Link>
      ) : (
        getLogoVariant()
      )}
    </Flex>
  );
};

export default Logo;
